'use client'

// oxlint-disable-next-line cf-web/banned-imports
import { SpinnerIcon } from 'primitives/icons'
import * as React from 'react'
import type { InstantSearchServerState } from 'react-instantsearch'
import {
  Configure,
  Highlight,
  useInstantSearch,
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch'
import type { Hit } from 'instantsearch.js/es/types'
import debounce from 'lodash.debounce'
import { algoliaTemplatesIndex } from 'algolia/algolia-templates'
import { defaultSearchClient } from 'algolia/algolia'
import { useAutocomplete } from 'algolia/algolia.components'
import { InsightsMiddleware } from 'algolia/insights-middleware'
import { tw } from 'utils/classnames'

import type { SearchInputProps } from 'components/search-input'
import { SearchInput as SearchInputComponent, SearchInputFallback } from 'components/search-input'
import {
  StudioTemplatesSearchInput as StudioTemplatesSearchInputComponent,
  StudioTemplatesSearchInputFallback,
} from 'components/search-input/search-input-homepage'

import type { AlgoliaTemplateHit } from '../context/templates-page'
import { getUrlFromTitleId } from '../search-results/utils'

//InstantSearch component is not rendering the children until a search is started
//This is a workaround to render the children without a search as we trick it to think it has initial results from SSR
//This will fix the 1 sec blink of the Header search box
const initialAlgoliaData: InstantSearchServerState = {
  initialResults: {
    [algoliaTemplatesIndex]: {
      state: {
        facets: [],
        disjunctiveFacets: [],
        disjunctiveFacetsRefinements: {},
        hierarchicalFacets: [],
        facetsRefinements: {},
        facetsExcludes: {},
        numericRefinements: {},
        tagRefinements: [],
        hierarchicalFacetsRefinements: {},
        index: algoliaTemplatesIndex,
        clickAnalytics: true,
        maxValuesPerFacet: 20,
        query: '',
        highlightPreTag: '__ais-highlight__',
        highlightPostTag: '__/ais-highlight__',
      },
      results: [
        {
          query: '',
          hits: [],
          index: algoliaTemplatesIndex,
          hitsPerPage: 5,
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          exhaustiveNbHits: false,
          params: '',
        },
      ],
    },
  },
}

function getSearch() {
  if (typeof window !== 'undefined') {
    return window.location.search
  }
  return ''
}

type AlgoliaSearchVariant = 'default' | 'homepage'
interface AlgoliaSearchInputProps {
  NoResults: React.ReactNode
  searchComponentProps: SearchInputProps
  resultClassName?: string
  withoutResults?: boolean
  templatesGalleryRoute: string
  templatesItemRoute: string
  setTemplatesPathUrl?: (url: string) => void
  Link: React.ElementType
  variant?: AlgoliaSearchVariant
  selectedCategory?: string
  setSelectedCategory?: React.Dispatch<React.SetStateAction<string>>
}
export function AlgoliaSearchInput({
  NoResults,
  searchComponentProps,
  resultClassName,
  withoutResults = false,
  templatesGalleryRoute,
  templatesItemRoute,
  setTemplatesPathUrl,
  Link,
  variant,
  selectedCategory,
  setSelectedCategory,
}: AlgoliaSearchInputProps) {
  const searchParams = new URLSearchParams(getSearch())
  const { indices, refine } = useAutocomplete()
  const [inputValue, setInputValue] = React.useState(
    withoutResults ? searchParams?.get('query') || '' : '',
  )

  // make sure template is from creative fabrica and that either a category is not selected ie. it's all templates
  // or a category is selected and only templates from that category are rendered.
  const hits = React.useMemo(
    () =>
      (indices[0].hits as unknown as Hit<AlgoliaTemplateHit>[]).filter(
        (hit, index) =>
          hit.imageUrl.includes('creativefabrica') &&
          (!selectedCategory ||
            (selectedCategory &&
              hit.category.trim().toLowerCase() === selectedCategory.trim().toLowerCase() &&
              index < 5)),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indices?.[0], selectedCategory, inputValue],
  )
  const [showResults, setShowResults] = React.useState(false)
  const { status } = useInstantSearch()
  const routeToTemplate = React.useMemo(
    () =>
      selectedCategory
        ? `${templatesGalleryRoute}${selectedCategory}${inputValue ? `?query=${inputValue}` : ''}`
        : `${templatesGalleryRoute}${inputValue ? `?query=${inputValue}` : ''}`,
    [templatesGalleryRoute, inputValue, selectedCategory],
  )

  React.useEffect(() => {
    setTemplatesPathUrl?.(routeToTemplate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeToTemplate])

  const handleAlgoliaAutocomplete = (query: string) => {
    refine(query)
    handleShowResults(query)
  }

  //eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAlgoliaSearch = React.useCallback(debounce(handleAlgoliaAutocomplete, 300), [])

  function handleShowResults(value: string) {
    setShowResults(Boolean(value?.length >= 3))
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value)

    if (e.target.value.length === 0 || e.target.value.length >= 3) {
      debouncedAlgoliaSearch(e.target.value)
    } else {
      setShowResults(false)
    }
  }

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !withoutResults) {
      window.location.assign(routeToTemplate)

      return
    }
  }

  function handleResults() {
    if (inputValue.length < 3) {
      return null
    }

    if (status !== 'idle') {
      return (
        <>
          <span className="sr-only">Loading</span>
          <SpinnerIcon className="m-auto h-[16px] w-[16px] animate-spin text-gray-300" />
        </>
      )
    }

    if (status === 'idle' && hits && hits.length > 0) {
      return hits.map(hit => (
        <AutocompleteHit
          key={hit.objectID}
          href={`${templatesItemRoute}${getUrlFromTitleId(hit.title, hit.objectID)}`}
          hit={hit}
          Link={Link}
          className={resultClassName}
        />
      ))
    }

    return <div className="text-12 px-1 py-2">{NoResults}</div>
  }
  if (variant === 'homepage') {
    return (
      <StudioTemplatesSearchInputComponent
        showResults={showResults}
        setShowResults={setShowResults}
        value={inputValue}
        routeToTemplate={routeToTemplate}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onFocus={() => handleShowResults(inputValue)}
        withoutResults={withoutResults}
        maxLength={500}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        {...searchComponentProps}
      >
        {withoutResults ? null : handleResults()}
      </StudioTemplatesSearchInputComponent>
    )
  }

  return (
    <SearchInputComponent
      showResults={showResults}
      setShowResults={setShowResults}
      value={inputValue}
      routeToTemplate={routeToTemplate}
      onChange={handleChange}
      onKeyUp={handleKeyUp}
      onFocus={() => handleShowResults(inputValue)}
      withoutResults={withoutResults}
      maxLength={500}
      {...searchComponentProps}
    >
      {withoutResults ? null : handleResults()}
    </SearchInputComponent>
  )
}

type TemplatesSearchVariant = 'default' | 'homepage'
interface TemplatesSearchWithProviderProps {
  searchComponentProps: SearchInputProps
  resultClassName?: string
  NoResults: React.ReactNode
  templatesGalleryRoute: string
  templatesItemRoute: string
  Link: React.ElementType
  setTemplatesPathUrl?: (url: string) => void
  variant?: TemplatesSearchVariant
  selectedCategory?: string
  setSelectedCategory?: React.Dispatch<React.SetStateAction<string>>
}
export function TemplatesSearchWithProvider({
  NoResults,
  searchComponentProps,
  resultClassName,
  variant,
  setTemplatesPathUrl,
  selectedCategory,
  setSelectedCategory,
  ...props
}: TemplatesSearchWithProviderProps) {
  const searchParams = new URLSearchParams(getSearch())

  const initialState = {
    [algoliaTemplatesIndex]: {
      query: searchParams?.get('query') || '',
    },
  }

  return (
    <InstantSearchSSRProvider {...initialAlgoliaData}>
      <InstantSearch
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        searchClient={defaultSearchClient}
        indexName={algoliaTemplatesIndex}
        initialUiState={initialState}
      >
        <Configure
          hitsPerPage={5}
          filters={selectedCategory ? `category:"${selectedCategory}"` : undefined}
        />
        <React.Suspense
          fallback={
            variant === 'homepage' ? (
              <StudioTemplatesSearchInputFallback {...searchComponentProps} />
            ) : (
              <SearchInputFallback {...searchComponentProps} />
            )
          }
        >
          <AlgoliaSearchInput
            variant={variant}
            searchComponentProps={searchComponentProps}
            resultClassName={resultClassName}
            NoResults={NoResults}
            setTemplatesPathUrl={setTemplatesPathUrl}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            {...props}
          />
        </React.Suspense>
        <InsightsMiddleware />
      </InstantSearch>
    </InstantSearchSSRProvider>
  )
}

export function AutocompleteHit({
  href,
  hit,
  Link,
  className,
}: {
  href: string
  hit: Hit<AlgoliaTemplateHit>
  Link: React.ElementType
  className?: string
}) {
  const LinkComponent = Link as React.ComponentType<{ href: string; children: React.ReactNode }>
  return (
    <LinkComponent href={href}>
      <div
        className={tw(
          'flex w-full flex-wrap items-center gap-4 bg-white px-4 py-2 hover:bg-gray-800',
          className,
        )}
      >
        <img
          src={hit.imageUrl}
          width={hit.width}
          height={hit.height}
          alt={hit.title}
          className="h-[50px] w-auto"
        />
        <Highlight
          attribute="title"
          hit={hit}
          highlightedTagName="span"
          nonHighlightedTagName="span"
          classNames={{ highlighted: 'font-bold' }}
        />
      </div>
    </LinkComponent>
  )
}
